<template>
  <v-dialog
    v-model="dialog"
    max-width="650px"
    click:outside="submit"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        :text="!hasOptions"
        color="primary"
        class="mx-2"
        v-on="on"
      >
        <v-icon
          left
          small
        >
          mdi-cog
        </v-icon>
        <span class="caption">options</span>
      </v-btn>
    </template>
    <v-card>
      <v-card-title>Field options : {{ field.name }} ({{ field.type }})</v-card-title>
      <v-divider />
      <v-card-text class="pb-0">
        <v-container class="text-center pb-0">
          <v-row dense>
            <template v-if="field.type == 'foreign_key' && possibleForeignDatasets.length<=0">
              <v-col
                cols="12"
                class="my-3"
              >
                There no database available for this relation <br>
                <v-btn
                  class="mt-3"
                  :to="{name:'CreateDataset'}"
                  target="_blank"
                  small
                  text
                >
                  <v-icon left>
                    mdi-plus
                  </v-icon>
                  create a new dataset
                </v-btn>
              </v-col>
            </template>
            <template v-else-if="field.type == 'foreign_key' && option">
              <v-col cols="12">
                <v-autocomplete
                  v-model="$v.option['dataset']['name'].$model"
                  clearable
                  :error-messages="!$v.option.dataset.name.required && $v.option.dataset.name.$anyDirty ?['This field is required']:[] "
                  outlined
                  :items="_.map(possibleForeignDatasets,'name')"
                  hide-no-data
                  :disabled="!field.isNew"
                  label="Target dataset"
                />
              </v-col>
              <v-col
                cols="12"
                class="mt-0 pt-0"
              >
                <v-select
                  v-model="$v.option['id'].$model"
                  clearable
                  outlined
                  :error-messages="!$v.option.id.required && $v.option.id.$anyDirty ?['This field is required']:[] "
                  class="mt-0"
                  label="Foreign field"
                  item-text="name"
                  item-value="id"
                  :disabled="!option['dataset']['name'] || !field.isNew"
                  :items="foreign_fields"
                />
              </v-col>

              <v-col
                cols="12"
                class="text-left"
              >
                <div class="subtitle-1">
                  Event constraints
                </div>
              </v-col>
              <v-col
                cols="6"
                class="mt-0 pt-0"
              >
                <v-select
                  v-model="option.on_update_policy"
                  :error-messages="!$v.option.on_update_policy.required ?['This field is required']:[] "
                  outlined
                  class="mt-0"
                  label="On update"
                  :disabled="!field.isNew"
                  :items="behaviorTriggerOptions"
                />
              </v-col>
              <v-col
                cols="6"
                class="mt-0 pt-0"
              >
                <v-select
                  v-model="option.on_delete_policy"
                  :error-messages="!$v.option.on_delete_policy.required ?['This field is required']:[] "
                  outlined
                  class="mt-0"
                  label="On delete"
                  :disabled="!field.isNew"
                  :items="behaviorTriggerOptions"
                />
              </v-col>
              <v-col
                cols="12"
              >
                <v-radio-group
                  v-model="option['foreign_key_type']"
                  class="mt-0 pt-0"
                >
                  <template v-slot:label>
                    <div
                      class="subtitle-1"
                    >
                      Type of relationship
                    </div>
                  </template>
                  <v-radio
                    v-for="(r,i) in typesOfRelationship"
                    :key="i"
                    :label="r.label"
                    :value="r.value"
                    :disabled="!field.isNew"
                  />
                </v-radio-group>
              </v-col>
            </template>
            <template v-else-if="options.length>0">
              <v-col
                v-for="(o,index) in options"
                :key="index"
                cols="12"
                class="py-0 my-0"
              >
                <field-option
                  :constraint="field.constraints?field.constraints.find(c=>c.name==o.name):null"
                  :type="field.type"
                  :option="o"
                  @update="v=>option[o.name] = v"
                />
              </v-col>
            </template>
            <template v-else>
              <div class="mb-5">
                No options available
              </div>
            </template>
            <v-col cols="12">
              <div>* Make sure to apply your modifications when you're finished.</div>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-container class="py-0">
          <v-row class="py-0">
            <v-col class="text-center pt-0">
              <v-btn
                color="primary"
                class="mr-2"
                @click="submit"
              >
                Close
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import FieldOption from './FieldOption'
  import { validationMixin } from 'vuelidate'
  import { required } from 'vuelidate/lib/validators'
  import DatasetService from './services/DatasetService'
  export default {
    name: 'FieldSettingsBtn',
    components: {
      FieldOption,
    },
    mixins: [validationMixin],
    validations: {
      option: {
        on_delete_policy: { required },
        on_update_policy: { required },
        dataset: { name: { required } },
        id: { required },
        foreign_key_type: { required },

      },
      validationGroup: ['option'],
    },
    props: {
      datasetName: {
        type: String,
        default: null,
      },
      datasetId: {
        type: String,
        default: null,
      },
      options: {
        type: Array,
        default: () => [],
      },
      field: {
        type: Object,
        default: () => {},
      },
      disabled: {
        type: Boolean,
        default: false,
      },
    },
    data () {
      return {
        option: {},
        dialog: false,
        possibleForeignDatasets: [],
        behaviorTriggerOptions: ['CASCADE', 'RESTRICT', 'SET NULL', 'NO ACTION'],
        typesOfRelationship: [
          {
            label: 'One to One',
            value: 'one-to-one',
          },
          {
            label: 'Many to one',
            value: 'many-to-one',
          },
        ],
      }
    },
    computed: {
      areOptionsValid () {
        return !this.$v.option.$invalid || !this.option
      },
      hasOptions () {
        return this.field.type === 'foreign_key'
      },
      foreign_fields () {
        let fields = []
        if (this.field.type !== 'foreign_key') { return fields }
        const found = this.possibleForeignDatasets.find(o => o.name === this.option.dataset.name)
        if (found) { fields = found.possible_fk_fields }
        return fields
      },
    },
    watch: {
      'field.type': {
        deep: true,
        handler (nv, ov) {
          if (nv === 'foreign_key') {
            this.dialog = true
            this.getPossibleForeignDatasets()
            this.setForeignKeyOptions()
          } else {
            this.setKeyOptions()
          }
        },

      },
    },
    mounted () {
      this.getPossibleForeignDatasets()
      this.setForeignKeyOptions()
      this.setKeyOptions()
    },
    methods: {
      setKeyOptions () {
        const self = this
        if (self.field.type !== 'foreign_key') {
          self.option = {}
          this.options.map(o => {
            self.option[o.name] = ''
          })
        }
      },
      setForeignKeyOptions () {
        const f = this.field
        if (f.type === 'foreign_key') {
          this.option = {
            on_delete_policy: f.foreign_key ? f.foreign_key.on_delete_policy : 'CASCADE',
            on_update_policy: f.foreign_key ? f.foreign_key.on_update_policy : 'CASCADE',
            dataset: f.foreign_key ? f.foreign_key.dataset : { name: null },
            id: f.foreign_key ? f.foreign_key.id : null,
            foreign_key_type: f.foreign_key ? f.foreign_key.foreign_key_type : 'one-to-one',
          }
        }
      },
      submit () {
        const self = this
        this.dialog = false
        let result = {}
        if (this.field.type === 'foreign_key') {
          result = this.option
        } else {
          result = _.map(Object.keys(this.option), (key) => {
            return {
              name: key,
              value: self.option[key],
            }
          })
        }
        this.$emit('update', result)
      },
      getPossibleForeignDatasets () {
        const self = this
        if (this.field.type === 'foreign_key' && this.possibleForeignDatasets.length <= 0) {
          DatasetService.possible_foreign_datasets(self).then(r => {
            console.log(r)
            this.possibleForeignDatasets = r.data.results.filter(o => o.hmd_id !== self.datasetId)
          })
        }
      },
    },
  }
</script>
